import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { apiClient, apiErrorMessage } from "../api/client";
import { absent, roleWords, shortDate } from "../lib/format";
import { useListParams } from "../lib/useListParams";
import { sortRows } from "../lib/sortRows";
import { csvFilename, downloadCsv, toCsv } from "../lib/csv";
import { Button } from "../ui/Button";
import { DataTable, type ColumnSpec } from "../ui/DataTable";
import { Toolbar } from "../ui/Toolbar";
import { factorWord } from "../ui/words";
import { runWords, useCampaignLabels } from "../lib/campaignLabels";
import { campaignPath, fileWord, stageTone } from "../lib/campaign";
import { Badge } from "../ui/Badge";
import { RecordLink } from "./RecordLink";
import { Spinner } from "./Spinner";

/* The campaigns a record has appeared in, as a table on every record page.
 *
 * From the 2026-10-08 audit: a rep on a person asked "why am I calling this
 * one" and a builder on a company asked "have we already run at these
 * people", and the lake had the answer in lake.campaign_member (every
 * building a run produced, with its rank, score and the score's breakdown by
 * factor) while the pages showed none of it. api.record_campaigns (engine
 * migration 183) returns one row per campaign and building the record
 * reaches, newest first, with the basis on which it reaches the building: a
 * building is itself, a company reaches its buildings through its role on
 * them, and a person through a project a source named them on or through
 * their employer's buildings.
 *
 * The "Why" column is the three factors that counted most in the member's
 * score, in words, which is the one-line answer to the rep's question.
 *
 * Each row also says the campaign's stage and who owns it now, and on a
 * person, when an export sent them out and who owned the campaign then
 * (engine migration 217), so somebody in another territory sees who already
 * reached out (James, September 25). */

/* The row api.record_campaigns returns, written here because the type
   generator reads a function that returns a table as unknown. The columns
   are the function's, in schema/183_record_campaigns.sql. */
export type CampaignRow = {
  campaign_id: string;
  campaign_name: string | null;
  frozen_at: string | null;
  destination: string | null;
  member_count: number | null;
  rank: number | null;
  score: number | string | null;
  breakdown: unknown;
  property_id: string | null;
  property_name: string | null;
  property_city: string | null;
  property_state: string | null;
  basis: string | null;
  // Added by engine migration 217: the campaign's slug and name, its stage,
  // its owner now, and for a person, when an export of it sent them out and
  // who owned the campaign then.
  criteria_set_name: string | null;
  campaign_label: string | null;
  stage: string | null;
  stage_word: string | null;
  owner_name: string | null;
  sent_at: string | null;
  sent_owner_name: string | null;
};

const LIMIT = 500;

/** The record's campaigns, shared by the tab, the header's figure and the
 *  person page's one-line summary, so the three agree and it is one request. */
export function useRecordCampaigns(token: string, entityType: string, entityId: string) {
  return useQuery({
    queryKey: ["record-campaigns", entityType, entityId],
    queryFn: async () => {
      const { data, error } = await apiClient(token)
        .rpc("record_campaigns", { p_entity_type: entityType, p_entity_id: entityId }, { get: true })
        .limit(LIMIT);
      if (error) throw new Error(apiErrorMessage(error));
      return (data ?? []) as CampaignRow[];
    },
  });
}

/** How many distinct campaigns the rows name. */
export const campaignCount = (rows: readonly CampaignRow[] | undefined) =>
  rows ? new Set(rows.map((r) => r.campaign_id)).size : null;

/** The three factors that counted most, in words. */
export function topFactors(breakdown: unknown, n = 3): string {
  if (!breakdown || typeof breakdown !== "object") return "";
  return Object.entries(breakdown as Record<string, unknown>)
    .filter((e): e is [string, number] => typeof e[1] === "number" && e[1] > 0)
    .sort((a, b) => b[1] - a[1])
    .slice(0, n)
    .map(([k]) => factorWord(k))
    .join(", ");
}

function basisWords(basis: string | null | undefined): string {
  if (!basis || basis === "self") return "";
  if (basis === "project") return "A source named them here";
  if (basis === "employer") return "Through the employer";
  return `As ${roleWords([basis]) || basis}`;
}

export function CampaignsTable({ token, entityType, entityId }: { token: string; entityType: "company" | "person" | "property"; entityId: string }) {
  const params = useListParams("frozen_at", [], false, "", "cp.");
  const query = useRecordCampaigns(token, entityType, entityId);
  const labels = useCampaignLabels(token);
  const q = params.search.trim().toLowerCase();
  const all = query.data ?? [];
  const matching = q
    ? all.filter((r) => (r.campaign_label ?? "").toLowerCase().includes(q) || (r.campaign_name ?? "").toLowerCase().includes(q)
                     || (r.owner_name ?? "").toLowerCase().includes(q) || (r.property_name ?? "").toLowerCase().includes(q))
    : all;
  const rows = sortRows(matching, params.sorts, (r, column) => {
    switch (column) {
      case "frozen_at": return r.frozen_at;
      case "campaign_name": return r.campaign_name;
      case "campaign_label": return r.campaign_label ?? r.campaign_name;
      case "stage": return r.stage_word;
      case "owner_name": return r.owner_name;
      case "sent_at": return r.sent_at;
      case "destination": return r.destination;
      case "rank": return r.rank;
      case "score": return r.score;
      case "property_name": return r.property_name;
      case "basis": return r.basis;
      default: return null;
    }
  }, [{ column: "frozen_at", ascending: false }, { column: "rank", ascending: true }]);

  const columns: ColumnSpec<CampaignRow>[] = [
    // The campaign opens its page, and the date opens the list or export
    // this row came from.
    { id: "campaign", header: "Campaign", role: "leading", fields: ["campaign_label"], sort: "campaign_label", title: true, narrowLabel: null,
      cell: (r) => r.criteria_set_name
        ? <Link to={campaignPath(r.criteria_set_name)} className="link">{r.campaign_label ?? r.criteria_set_name}</Link>
        : r.campaign_name
          ? <Link to={`/runs/${r.campaign_id}`} className="link">{runWords(r.campaign_name, r.campaign_name.split(", ")[0], labels.data)}</Link>
          : absent() },
    { id: "run", header: "Run", role: "leading", fields: ["frozen_at"], sort: "frozen_at", sortDescendingFirst: true, narrowLabel: null,
      cell: (r) => <Link to={`/runs/${r.campaign_id}`}>{shortDate(r.frozen_at)}</Link> },
    { id: "stage", header: "Stage", role: "secondary", fields: ["stage_word"], sort: "stage", narrowLabel: null,
      cell: (r) => r.stage_word ? <Badge tone={r.stage === "archived" ? "quiet" : stageTone(r.stage)}>{r.stage_word}</Badge> : absent() },
    { id: "owner", header: "Owner", role: "secondary", fields: ["owner_name"], sort: "owner_name",
      cell: (r) => r.owner_name ?? <span className="muted">None</span> },
    ...(entityType === "person" ? [
      { id: "sent", header: "Sent out", role: "secondary", fields: ["sent_at"], sort: "sent_at", sortDescendingFirst: true,
        cell: (r: CampaignRow) => r.sent_at
          ? <>{shortDate(r.sent_at)}{r.sent_owner_name ? <span className="muted"> · {r.sent_owner_name}</span> : null}</>
          // A list is not a file, and an export before 2026-10-12 recorded
          // nobody, so a row without a record claims nothing.
          : r.destination && r.destination !== "review" ? <span className="muted">Not recorded</span> : null } as ColumnSpec<CampaignRow>,
    ] : []),
    { id: "rank", header: "Rank", role: "secondary", fields: ["rank"], align: "num", sort: "rank",
      cell: (r) => r.rank === null || r.rank === undefined ? absent()
        : <>{r.rank.toLocaleString()}<span className="muted"> of {(r.member_count ?? 0).toLocaleString()}</span></> },
    { id: "score", header: "Score", role: "secondary", fields: ["score"], align: "num", sort: "score", sortDescendingFirst: true,
      cell: (r) => r.score === null || r.score === undefined ? absent() : Number(r.score).toFixed(1) },
    { id: "why", header: "Why", role: "secondary", fields: ["breakdown"], narrowLabel: null,
      cell: (r) => topFactors(r.breakdown) || absent() },
    ...(entityType === "property" ? [] : [
      { id: "property", header: "Property", role: "secondary", fields: ["property_name"], sort: "property_name", narrowLabel: "at",
        cell: (r: CampaignRow) => <RecordLink to="properties" id={r.property_id} label={r.property_name} /> } as ColumnSpec<CampaignRow>,
      { id: "basis", header: "Through", role: "detail", fields: ["basis"], sort: "basis",
        cell: (r: CampaignRow) => basisWords(r.basis) || absent() } as ColumnSpec<CampaignRow>,
    ]),
    { id: "destination", header: "Sent to", role: "detail", fields: ["destination"], sort: "destination",
      cell: (r) => !r.destination || r.destination === "review" ? <span className="muted">Not sent</span> : fileWord(r.destination) },
  ];

  // Counted from what loaded, so the line says nothing until it has.
  const count = campaignCount(query.data);
  return (
    <section className="rp-tabbody" aria-label="Campaigns">
      <Toolbar
        id="campaigns"
        search={{ label: "Search by campaign, owner or property", value: params.search, onSubmit: (s) => params.set({ search: s }) }}
        onClearAll={() => params.set({ search: "" })}
        right={rows.length ? (
          <Button size="compact-sm" onClick={() => downloadCsv(csvFilename("campaigns"),
            toCsv(["campaign_label", "stage_word", "owner_name", "campaign_name", "frozen_at", "destination", "sent_at", "sent_owner_name",
                   "rank", "member_count", "score", "property_name", "basis"],
                  rows as unknown as Record<string, unknown>[]))}>
            CSV
          </Button>
        ) : null}
      />
      <p className="muted rp-count">
        {count === null ? "" : `In ${count.toLocaleString()} campaign${count === 1 ? "" : "s"}. `}
        {entityType === "property" ? "Each row is one list, with where this property ranked and why."
          : "Each row is one list and one property, with where the property ranked and why."}
        {all.length >= LIMIT ? ` Showing the newest ${LIMIT.toLocaleString()} rows.` : ""}
      </p>
      {query.isLoading ? <p className="muted"><Spinner />Loading.</p>
        : query.isError ? <p className="refusal">{(query.error as Error).message}</p>
        : !rows.length ? (
          <p className="muted">
            {q ? "No campaign matches that search."
              : <>Not in any campaign yet. A campaign is a saved search the engine runs, and the <Link to="/campaigns">Campaigns screen</Link> builds one.</>}
          </p>
        )
        : <DataTable columns={columns} rows={rows} rowKey={(r) => `${r.campaign_id}|${r.property_id}`} params={params} />}
    </section>
  );
}
